/**
 * Integración con WordPress para Participation Inquiry App
 * Este archivo maneja toda la comunicación entre la app React y WordPress
 */

// ========================================
// TIPOS PARA LA COMUNICACIÓN CON WORDPRESS
// ========================================

export interface WordPressFormData {
  name: string;
  lastname: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  job_title: string;
  area: string;
  subarea: string;
  organization_type: string;
  industries: string;
  participation_modality: string;
  catalogo: string;
  programa: string;
  objectives: string; // Mantener para compatibilidad
  interest_areas: string;
  message: string;
  participation_options: string;
  autorizo_whatsapp: boolean;
  deseo_novedades: boolean;
}

export interface HeightChangeMessage {
  type: 'iframe-height-change';
  height: number;
}

export interface FormSubmissionMessage {
  type: 'participation-form-submission';
  formData: WordPressFormData;
  action?: 'replace' | 'add' | 'new';
  previousQueryId?: string;
}

export interface IframeReadyMessage {
  type: 'iframe-ready';
  height: number;
}

// ========================================
// FUNCIONES DE COMUNICACIÓN
// ========================================

/**
 * Envía un mensaje al iframe padre (WordPress)
 */
export const sendMessageToParent = (message: any) => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, '*');
  }
};

/**
 * Envía datos del formulario a WordPress
 */
export const submitFormToWordPress = (
  formData: WordPressFormData, 
  action: 'replace' | 'add' | 'new' = 'new',
  previousQueryId?: string
) => {
  const message: FormSubmissionMessage = {
    type: 'participation-form-submission',
    formData: formData,
    action: action,
    previousQueryId: previousQueryId
  };
  
  sendMessageToParent(message);
};

const MIN_IFRAME_HEIGHT = 600;

const getContentHeight = (): number => {
  const body = document.body;
  const html = document.documentElement;
  const height = Math.max(
    body.scrollHeight,
    body.offsetHeight,
    html.scrollHeight,
    html.offsetHeight
  );
  // Margen inferior extra para mensaje de éxito y evitar recorte en el iframe
  return Math.max(height + 48, MIN_IFRAME_HEIGHT);
};

// Variable para almacenar la última altura notificada
let lastNotifiedHeight = 0;

/**
 * Notifica cambios de altura del iframe
 */
// Variable para controlar debounce
let heightChangeTimeout: NodeJS.Timeout | null = null;

export const notifyHeightChange = () => {
  // Debounce para evitar llamadas excesivas
  if (heightChangeTimeout) {
    clearTimeout(heightChangeTimeout);
  }
  
  heightChangeTimeout = setTimeout(() => {
    // Usar requestAnimationFrame para asegurar que el DOM esté actualizado
    requestAnimationFrame(() => {
      const height = getContentHeight();
      const heightDifference = Math.abs(height - lastNotifiedHeight);

      if (heightDifference > 30) {
        lastNotifiedHeight = height;
        
        const message: HeightChangeMessage = {
          type: 'iframe-height-change',
          height: height
        };
        
        sendMessageToParent(message);
      }
    });
  }, 150); // Debounce aumentado a 150ms para mayor estabilidad
};

/**
 * Notifica que el iframe está listo
 */
export const notifyIframeReady = () => {
  // Esperar a que el contenido esté completamente renderizado
  setTimeout(() => {
    const height = getContentHeight();
    lastNotifiedHeight = height;
    
    const message: IframeReadyMessage = {
      type: 'iframe-ready',
      height: height
    };
    
    sendMessageToParent(message);
  }, 500); // Esperar 500ms para que el contenido se renderice
};

// ========================================
// FUNCIONES DE UTILIDAD
// ========================================

/**
 * Mapea los datos del formulario React al formato de WordPress
 */
export const mapFormDataToWordPress = (
  reactFormData: any,
  selectedOptions: any[] = []
): WordPressFormData => {
  // Convertir opciones de participación a string con solo nombres separados por coma
  const optionsString = selectedOptions.map(option => option.name).join(', ');
  
  return {
    name: reactFormData.nombre || '',
    lastname: reactFormData.apellido || '',
    email: reactFormData.email,
    phone: reactFormData.telefono,
    company: reactFormData.organizacion,
    website: reactFormData.sitioWeb || '',
    job_title: reactFormData.cargo || '',
    area: reactFormData.area || '',
    subarea: reactFormData.subarea || '',
    organization_type: reactFormData.tipoOrganizacion || '',
    industries: reactFormData.industrias ? reactFormData.industrias.join(', ') : '',
    participation_modality: reactFormData.modalidadParticipacion ? reactFormData.modalidadParticipacion.join(', ') : '',
    catalogo: reactFormData.catalogo ? reactFormData.catalogo.join(', ') : '',
    programa: reactFormData.programa ? reactFormData.programa.join(', ') : '',
    objectives: reactFormData.objetivos ? reactFormData.objetivos.join(', ') : '', // Mantener para compatibilidad
    interest_areas: reactFormData.ejesInteres ? reactFormData.ejesInteres.join(', ') : '',
    message: reactFormData.mensaje || '',
    participation_options: optionsString,
    autorizo_whatsapp: Boolean(reactFormData.autorizoWhatsApp),
    deseo_novedades: Boolean(reactFormData.deseoNovedades)
  };
};

/**
 * Sistema unificado de observación de altura
 */
let heightObserver: MutationObserver | null = null;

const setupHeightObserver = () => {
  if (heightObserver) {
    heightObserver.disconnect();
  }
  
  heightObserver = new MutationObserver(() => {
    // Solo notificar cambios si el contenido realmente cambió
    setTimeout(() => {
      notifyHeightChange();
    }, 100);
  });
  
  heightObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: false // Solo observar cambios en la estructura, no en atributos
  });
};

/**
 * Configura los listeners para la comunicación con WordPress
 */
export const setupWordPressIntegration = (
  onIframeReady?: (height: number) => void
) => {
  // Listener para mensajes del iframe padre
  const handleMessage = (event: MessageEvent) => {
    if (event.data?.type === 'request-height') {
      notifyHeightChange();
      notifyIframeReady();
      return;
    }
    if (event.data?.type === 'iframe-ready' && onIframeReady) {
      onIframeReady(event.data.height);
    }
  };
  
  window.addEventListener('message', handleMessage);
  
  // Notificar altura inicial con múltiples intentos
  const notifyInitialHeight = () => {
    // Primer intento inmediato
    notifyIframeReady();
    
    // Segundo intento después de 1 segundo
    setTimeout(() => {
      notifyIframeReady();
    }, 1000);
    
    // Tercer intento después de 2 segundos
    setTimeout(() => {
      notifyIframeReady();
    }, 2000);

    setTimeout(() => {
      notifyIframeReady();
    }, 4000);
  };
  
  notifyInitialHeight();
  
  // Configurar observador de altura unificado
  setupHeightObserver();
  
  // Listener para cambios de orientación en mobile
  const handleOrientationChange = () => {
    setTimeout(() => {
      notifyHeightChange();
    }, 200);
  };
  
  // Listener para resize
  const handleResize = () => {
    setTimeout(() => {
      notifyHeightChange();
    }, 100);
  };
  
  window.addEventListener('orientationchange', handleOrientationChange);
  window.addEventListener('resize', handleResize);
  
  // Cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
    window.removeEventListener('orientationchange', handleOrientationChange);
    window.removeEventListener('resize', handleResize);
    if (heightObserver) {
      heightObserver.disconnect();
      heightObserver = null;
    }
  };
};

/**
 * Función para mostrar notificaciones (opcional)
 */
export const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
  // Crear elemento de notificación
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 15px 20px;
    border-radius: 5px;
    color: white;
    font-weight: bold;
    z-index: 9999;
    max-width: 300px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    background: ${type === 'success' ? '#4CAF50' : '#f44336'};
    font-family: Arial, sans-serif;
  `;
  notification.textContent = message;
  
  document.body.appendChild(notification);
  
  // Auto-remover después de 5 segundos
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }, 5000);
};

// ========================================
// EXPORTAR FUNCIONES PRINCIPALES
// ========================================

const wordpressIntegration = {
  submitFormToWordPress,
  notifyHeightChange,
  notifyIframeReady,
  mapFormDataToWordPress,
  setupWordPressIntegration,
  showNotification
};

export default wordpressIntegration;
